import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  className?: string;
  variant?: 'default' | 'minimal' | 'fullscreen';
}

export function LoadingSpinner({ 
  size = 'md', 
  text, 
  className,
  variant = 'default' 
}: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-6 w-6', 
    lg: 'h-8 w-8'
  };

  const spinnerElement = (
    <Loader2 
      className={cn(
        'animate-spin text-muted-foreground',
        sizeClasses[size],
        className
      )} 
    />
  );

  if (variant === 'minimal') {
    return spinnerElement;
  }

  if (variant === 'fullscreen') {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          {text && (
            <p className="text-sm text-muted-foreground font-medium">
              {text}
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2 py-4">
      {spinnerElement}
      {text && (
        <span className="text-sm text-muted-foreground">
          {text}
        </span>
      )}
    </div>
  );
}

// Pre-defined loading states for common use cases
export function PageLoadingSpinner({ text = "Načítavam..." }: { text?: string }) {
  return (
    <div className="flex items-center justify-center min-h-[200px]">
      <LoadingSpinner size="lg" text={text} />
    </div>
  );
}

export function ButtonLoadingSpinner() {
  return <LoadingSpinner size="sm" variant="minimal" className="mr-2" />;
}

export function TableLoadingSpinner({ text = "Načítavam údaje..." }: { text?: string }) {
  return (
    <div className="flex items-center justify-center py-8">
      <LoadingSpinner text={text} />
    </div>
  );
}

export function FullscreenLoadingSpinner({ text = "Načítavam aplikáciu..." }: { text?: string }) {
  return <LoadingSpinner variant="fullscreen" text={text} />;
}
